import { Link } from "react-router-dom";
import { BarChart3, Clock, FileSpreadsheet, ChevronRight } from "lucide-react";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card } from "@/components/ui";
import { REPORT_DATASETS, DATASET_GROUPS } from "./reportDatasets";

// =============================================================================
// ReportsHubScreen — T-392  (/reports)
// The reporting front door: every tracked dataset as a card → generic
// explorer (filter + Excel export), plus the purpose-built report builders
// (man-hours, client hours) that already existed. Manager+.
// =============================================================================

export default function ReportsHubScreen() {
  const user = useRequireAuth();
  const canView = hasRole(user, "manager");

  if (!canView) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card><div className="p-6 text-sm text-text-muted">Reports are restricted to managers and directors.</div></Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-[1100px] mx-auto pb-16">
      <div className="mb-6 flex items-center gap-3">
        <div className="w-11 h-11 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center">
          <BarChart3 size={22} className="text-accent" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Reports</h1>
          <p className="text-[13px] text-text-muted">
            Pick a dataset, filter it down, export it to Excel. Everything the platform tracks, in one place.
          </p>
        </div>
      </div>

      {/* Purpose-built reports first — richer than the generic explorer */}
      <h2 className="text-[11px] font-semibold text-text-muted uppercase tracking-wider2 mb-2 px-1">
        Purpose-built reports
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <Link to="/reports/man-hours" className="group">
          <Card className="h-full transition-colors group-hover:border-accent/50">
            <div className="p-4 flex items-start gap-3">
              <Clock size={18} className="text-accent flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[14px] flex items-center gap-1">
                  Man-hours by project <ChevronRight size={14} className="text-text-dim group-hover:text-accent" />
                </div>
                <div className="text-[12px] text-text-muted mt-0.5">
                  Client-facing hours summary — by project and by person, filtered by period.
                </div>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/reports/hours" className="group">
          <Card className="h-full transition-colors group-hover:border-accent/50">
            <div className="p-4 flex items-start gap-3">
              <FileSpreadsheet size={18} className="text-accent flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[14px] flex items-center gap-1">
                  Client hours reports <ChevronRight size={14} className="text-text-dim group-hover:text-accent" />
                </div>
                <div className="text-[12px] text-text-muted mt-0.5">
                  Actual vs reported snapshots with saved history — Excel + PDF.
                </div>
              </div>
            </div>
          </Card>
        </Link>
      </div>

      {/* Generic datasets, grouped */}
      {DATASET_GROUPS.map(group => {
        const sets = REPORT_DATASETS.filter(d => d.group === group);
        if (sets.length === 0) return null;
        return (
          <div key={group} className="mb-6">
            <h2 className="text-[11px] font-semibold text-text-muted uppercase tracking-wider2 mb-2 px-1">
              {group}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {sets.map(d => {
                const Icon = d.icon;
                return (
                  <Link key={d.id} to={`/reports/explore/${d.id}`} className="group">
                    <Card className="h-full transition-colors group-hover:border-accent/50">
                      <div className="p-4 flex items-start gap-3">
                        <Icon size={18} className="text-accent flex-shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-[14px] flex items-center gap-1">
                            {d.label}
                            <ChevronRight size={14} className="text-text-dim group-hover:text-accent" />
                          </div>
                          <div className="text-[12px] text-text-muted mt-0.5 leading-snug">{d.blurb}</div>
                        </div>
                      </div>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })}

      <div className="text-[11px] text-text-dim italic px-1">
        Missing a dataset you want to slice? Tell James's build chat — adding one is a registry entry, not a rebuild.
      </div>
    </div>
  );
}
